<template>
  <v-text-field
    v-model="internalValue"
    @blur="setQuantity(internalValue)"
    @keyup.enter="setQuantity(internalValue)"
    @focus="$event.target.select()"
    class="quantity-input"
    :class="sizeClass"
    type="number"
    :min="min"
    max="max"
    step="1"
    variant="outlined"
    density="compact"
    :hide-details="hideDetails"
    v-bind="$attrs"
    :hint="hint"
    persistent-hint
    :aria-describedby="inputAriaDescribedby"
    :aria-label="inputAriaLabel"
    :loading="loading"
  >
    <template v-slot:prepend>
      <v-btn
        icon
        variant="plain"
        :disabled="disableMinus"
        @click="setQuantity(value - 1)"
        :size="size"
        :aria-label="minusAriaLabel"
      >
        <v-icon>fa fa-minus</v-icon>
      </v-btn>
    </template>
    <template v-slot:append>
      <v-btn
        icon
        variant="plain"
        :disabled="disablePlus"
        @click="setQuantity(value + 1)"
        :size="size"
        :aria-label="plusAriaLabel"
      >
        <v-icon>fa fa-plus</v-icon>
      </v-btn>
    </template>
  </v-text-field>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
const props = withDefaults(
  defineProps<{
    value: number;
    min?: number;
    max?: number;
    hint?: string;
    hideDetails?: boolean;
    size?: "small" | "default";
    zeroOnMin?: boolean;
    inputAriaDescribedby?: string;
    inputAriaLabel?: string;
    minusAriaLabel?: string;
    plusAriaLabel?: string;
    loading?: boolean;
    disablePlus?: boolean;
  }>(),
  {
    min: 0,
    max: 999999,
    hint: "",
    hideDetails: true,
    size: "default",
    zeroOnMin: false,
    inputAriaLabel: "quantity",
    minusAriaLabel: "decrease quantity",
    plusAriaLabel: "increase quantity",
    inputAriaDescribedby: "",
    loading: false,
    disablePlus: false,
  },
);

const emit = defineEmits(["update", "value-out-of-bounds"]);

const internalValue = ref(props.value);

watch(
  () => props.value,
  (val) => {
    if (val !== internalValue.value) internalValue.value = val;
  },
);

function setQuantity(input: string | number) {
  let num = parseInt(input.toString());
  const originalValue = num;
  if (isNaN(num)) num = props.min;

  // Handle the zeroOnMin logic with direction awareness
  if (num < props.min) {
    if (props.zeroOnMin) {
      // If current value is 0 and we're trying to increase (num > 0), set to min
      if (internalValue.value === 0 && num > 0) {
        num = props.min;
      } else {
        // Otherwise, set to 0 when below min
        num = 0;
      }
    } else {
      num = props.min;
    }
    if (originalValue > 0) {
      emit("value-out-of-bounds", originalValue, props.min, props.max);
    }
  }

  if (num > props.max) {
    num = props.max;
    emit("value-out-of-bounds", originalValue, props.min, props.max);
  }

  internalValue.value = num;
  emit("update", num);
}

const sizeClass = computed(() =>
  props.size === "small" ? "quantity-input--small" : "",
);

const disableMinus = computed(() => {
  if (props.zeroOnMin) {
    return props.value <= 0;
  }
  return props.value <= props.min;
});
</script>

<style lang="scss">
.quantity-input {
  display: inline-flex;
  margin: auto !important;
  align-items: center !important;

  .v-input__control {
    width: 70px;
  }

  .v-input__prepend {
    margin-right: 0;
  }

  .v-input__append {
    margin-left: 0;
  }

  input {
    font-size: 1rem;
    text-align: center;
  }

  &.quantity-input--small {
    input {
      font-size: 0.75rem; // ~12px
      padding: 2px 0;
      text-align: center;
    }
    .v-field__input {
      padding-top: 0;
      padding-bottom: 0;
      min-height: 22px;
    }
  }

  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type="number"] {
    -moz-appearance: textfield;
  }
}
</style>
